import { useEffect, useRef } from 'react'
import { formatAddressLine } from '@/components/address/address-fields'
import { toServiceAddress, type ServiceAddress } from '@/components/service-address/service-address-section'
import { getAppointmentAddresses, type Address } from '@/lib/api/addresses'
import type { Catalog, CatalogVariation } from '@/lib/api/catalog/catalog-api'
import { getBilling, getPaymentOptions } from '@/lib/api/financial/financial-billing-api'
import { findOpenSales } from '@/lib/ai-user/open-sales'
import {
    collectToasts,
    registerScreenController,
    type AiSchedulingServiceType,
    type SchedulingActionResult,
    type SchedulingSnapshot,
    type SchedulingSubmitResult,
} from '@/lib/ai-user/screen-bridge'
import type { ServiceType } from './types'

/**
 * Controlador da tela de agendamento para o Modo IA (ver docs/ai-user.md, "Como ela
 * opera as telas"). Cada ação chama o MESMO handler da tela, à vista do supervisor,
 * espera a tela assentar (debounce da busca, cotação, hidratação do catálogo) e
 * devolve o retrato atualizado com os avisos que apareceram.
 *
 * Atende EXAME, CONSULTA e PROCEDIMENTO não cirúrgico, só em criação (fase 2). Em
 * edição ou orçamento a tela não registra o controlador. Cada tipo guarda os itens do
 * seu jeito na tela (exame em `examItems`, procedimento em `orderItems`, consulta num
 * item único com variação); a tela entrega aqui as LINHAS do tipo atual e os gestos de
 * remover e escolher variação, e a lógica de cada tipo continua nela.
 */

/** Tipos que a IA monta. Pacote e cirurgia ficam de fora (decisão do owner, 24/10/2026). */
export const AI_SCHEDULING_SERVICE_TYPES: readonly AiSchedulingServiceType[] = ['EXAM', 'CONSULTATION', 'PROCEDURE']

/** Linha da tela, do tipo atual. */
export interface SchedulingAiLine {
    catalogId: number
    name: string
    catalogTypeName: string
    unitPrice: number
    quantity: number
    catalogVariationId?: number
    variationName?: string
}

export interface SchedulingAiBridgeDeps {
    enabled: boolean
    serviceType: ServiceType
    /** Mesmo gesto de trocar o tipo na tela: limpa os itens. */
    setServiceType: (type: ServiceType) => void
    clientId: string
    clientName: string
    setClientId: (id: string) => void
    lines: SchedulingAiLine[]
    removeLine: (line: number) => void
    chooseLineVariation: (line: number, variation: CatalogVariation) => void
    variationsByCatalog: Record<number, CatalogVariation[]>
    catalogsById: Record<number, Catalog>
    /** Busca do catálogo SEM recorte de tipo (exame, consulta e procedimento); entra no mapa de catálogos. */
    searchAllCatalogs: (query: string) => Promise<Catalog[]>
    /** Mesmo handler do clique no catálogo: põe o item no tipo atual da tela. */
    selectCatalog: (catalog: Catalog) => Promise<void>
    importExamFile: (file: File) => Promise<void>
    importingExamDoc: boolean
    unmatchedImports: string[]
    /** Preço da variação para o executante escolhido (cascata parceiro > profissional > empresa). */
    variationPrice: (variation: CatalogVariation) => number
    /** Parceiros que fazem todos os itens (vínculos efetivos, com `partnerId` e `partner`). */
    activePartners: any[]
    /** Vínculos de parceiro que valem para a linha (os da variação, quando houver). */
    effectivePartnersFor: (item: SchedulingAiLine) => any[]
    /** Total de um parceiro para um conjunto de linhas (o mesmo cálculo do seletor da tela). */
    partnerTotalFor: (partnerId: number, items: SchedulingAiLine[]) => number
    partnerId: number | null
    setPartnerId: (id: number | null) => void
    /** Vínculos de profissional ativos dos itens (com `professionalId` e `professional`). */
    professionals: any[]
    professionalId: number | null
    setProfessionalId: (id: number | null) => void
    pricingReady: boolean
    chargedTotal: number
    appliedCoupon: { code: string; amount: number } | null
    applyCoupon: (code: string) => Promise<void>
    applyingCoupon: boolean
    grouping: { billingId: number; count: number; amount: number } | null
    chargeSeparately: () => void
    addressConfirmed: boolean
    missingPrices: string[]
    serviceAddress: ServiceAddress | null
    setServiceAddress: (address: ServiceAddress | null) => void
    paymentMethod: string
    installmentCount: number | null
    deferBilling: boolean
    /** Mesmo gesto do bloco de pagamento da tela (`PaymentOptions`). */
    choosePayment: (method: string, installments: number) => void
    /** "Gerar cobrança depois". */
    payLater: () => void
    /** "Outra forma ou dividir o pagamento". */
    payOnBillingScreen: () => void
    setPipelineId: (id: number | undefined) => void
    submitting: boolean
    /**
     * Envio pelo botão "Agendar" ou, com `addAnother`, "Adicionar outro"; `null` = nada foi
     * criado (a tela mostrou o motivo).
     */
    submit: (addAnother: boolean) => Promise<{ billingId: number | null; detailPath: string | null } | null>
}

/**
 * Formas que a IA lança na tela. O pagamento dividido vai para a tela da cobrança
 * (`payOnBillingScreen`); dinheiro e maquininha ficam com uma pessoa.
 */
const AI_PAYMENT_METHODS = ['PIX', 'CREDIT_CARD', 'DEBIT_CARD'] as const

/** Formas com link de pagamento (o mesmo critério do `BillingPaymentShare`). */
const CHECKOUT_LINK_METHODS = ['PIX', 'CREDIT_CARD', 'DEBIT_CARD', 'BOLETO']

/** Endereço cadastrado que pode ser o local do atendimento. */
interface AddressOption {
    id: number
    owner: 'partner' | 'professional'
    address: Address
}

/** Detalhe do atendimento criado: é para ele que a tela navega depois do envio. */
const CREATED_PATHS: Array<{ prefix: string; kind: 'EXAM' | 'CONSULTATION' | 'PROCEDURE' }> = [
    { prefix: '/bookings/exams/', kind: 'EXAM' },
    { prefix: '/bookings/consultations/', kind: 'CONSULTATION' },
    { prefix: '/bookings/orders/', kind: 'PROCEDURE' },
]

function createdFromPath(path: string | null): SchedulingSubmitResult['created'] {
    if (!path) return undefined
    for (const { prefix, kind } of CREATED_PATHS) {
        if (!path.startsWith(prefix)) continue
        const id = Number(path.slice(prefix.length).split(/[/?]/)[0])
        if (Number.isInteger(id) && id > 0) return { kind, id }
    }
    return undefined
}

/** Espera mínima depois de uma ação: cobre o debounce da busca (300 ms) e da cotação (400 ms). */
const SETTLE_MIN_MS = 500
const SETTLE_MAX_MS = 10_000
const SETTLE_TICK_MS = 100

/** Em `Partner`, `name` é o nome fantasia e `tradingName` a razão social (o inverso de `Company`). */
function partnerName(link: any): string {
    return link?.partner?.name || link?.partner?.tradingName || `Parceiro ${link?.partnerId}`
}

function partnerLegalName(link: any): string | null {
    const legal = link?.partner?.tradingName
    return legal && legal !== link?.partner?.name ? legal : null
}

/** Mesmo critério da tela: vínculo ativo e parceiro ativo. */
function isActiveLink(link: any): boolean {
    return link?.active !== false && link?.partner?.active !== false
}

/** Tipo de atendimento do item no catálogo; `null` = fora do que a IA monta. */
function aiServiceTypeOf(catalog: Catalog): AiSchedulingServiceType | null {
    const category = String(catalog.category || catalog.catalogType?.category || '')
    return (AI_SCHEDULING_SERVICE_TYPES as readonly string[]).includes(category)
        ? (category as AiSchedulingServiceType)
        : null
}

/**
 * A busca do catálogo casa por TRECHO (sem acento), e o nome de quem atende não é trecho
 * do nome da especialidade: "obstetra" não está em "Obstetrícia" nem em "Obstétrica", e
 * "ortopedista" não está em "Ortopedia". Palavra longa vira a raiz (70% do começo), que
 * casa as duas formas: "obstet", "ortopedi", "cardiologi". `null` = nada a encurtar.
 */
export function searchStem(query: string): string | null {
    const words = query.trim().split(/\s+/).filter(Boolean)
    let changed = false
    const stemmed = words.map(word => {
        if (word.length < 7) return word
        changed = true
        return word.slice(0, Math.ceil(word.length * 0.7))
    })
    return changed ? stemmed.join(' ') : null
}

/** Parceiros que fazem todas as linhas, com o vínculo do primeiro item (traz o nome). */
function intersectPartners(lines: SchedulingAiLine[], linksOf: (item: SchedulingAiLine) => any[]): any[] {
    if (lines.length === 0) return []
    const sets = lines.map(item => linksOf(item).filter(isActiveLink))
    return sets[0].filter(link => sets.every(set => set.some(other => other.partnerId === link.partnerId)))
}

export function useSchedulingAiBridge(deps: SchedulingAiBridgeDeps): void {
    const depsRef = useRef(deps)
    depsRef.current = deps
    /** Endereços do executante atual, pela mesma rota da seção de local da tela. */
    const addressesRef = useRef<{ key: string; options: AddressOption[] } | null>(null)
    /**
     * Cobrança que a IA abriu com "Adicionar outro" nesta tela. O agrupamento com ELA é o
     * grupo da venda; com qualquer outra cobrança do dia, a venda nova vai à parte.
     */
    const ownGroupBillingRef = useRef<number | null>(null)
    /** A IA escolheu decidir a forma na tela da cobrança (o estado da tela não distingue). */
    const payOnBillingScreenRef = useRef(false)

    useEffect(() => {
        if (!deps.enabled) return

        const executantKey = () => `${depsRef.current.partnerId ?? ''}|${depsRef.current.professionalId ?? ''}`

        /** Endereços cadastrados do parceiro e do profissional escolhidos. */
        const loadAddresses = async (): Promise<AddressOption[]> => {
            const d = depsRef.current
            const key = executantKey()
            if (addressesRef.current?.key === key) return addressesRef.current.options
            if (!d.partnerId && !d.professionalId) return []
            const response = await getAppointmentAddresses({
                partnerId: d.partnerId || undefined,
                professionalId: d.professionalId || undefined,
            })
            if (!response.success || !response.result) return []
            const options: AddressOption[] = [
                ...response.result.partner.map(address => ({ id: address.id, owner: 'partner' as const, address })),
                ...response.result.professional.map(address => ({
                    id: address.id,
                    owner: 'professional' as const,
                    address,
                })),
            ]
            addressesRef.current = { key, options }
            return options
        }

        const snapshot = (): SchedulingSnapshot => {
            const d = depsRef.current
            const partner = d.partnerId
                ? d.activePartners.find((link: any) => link.partnerId === d.partnerId)
                : undefined
            const itemType = (item: SchedulingAiLine) =>
                item.catalogTypeName || d.catalogsById[item.catalogId]?.catalogType?.name || 'Sem tipo'
            const types = [...new Set(d.lines.map(itemType))]
            const professional = d.professionalId
                ? d.professionals.find((link: any) => link.professionalId === d.professionalId)
                : undefined
            return {
                serviceType: d.serviceType,
                client: d.clientId ? { id: Number(d.clientId), name: d.clientName } : null,
                professionals: d.professionals
                    .filter((link: any) => link.professionalId)
                    .map((link: any) => ({
                        id: link.professionalId,
                        name: link.professional?.name || `Profissional ${link.professionalId}`,
                    })),
                professional: d.professionalId
                    ? {
                          id: d.professionalId,
                          name: professional?.professional?.name || `Profissional ${d.professionalId}`,
                      }
                    : null,
                items: d.lines.map((item, line) => ({
                    line,
                    catalogId: item.catalogId,
                    name: item.name,
                    type: itemType(item),
                    quantity: item.quantity,
                    unitPrice: item.unitPrice,
                    variation: item.catalogVariationId
                        ? { id: item.catalogVariationId, name: item.variationName || '' }
                        : null,
                    variationOptions: (d.variationsByCatalog[item.catalogId] || []).map(variation => ({
                        id: variation.id,
                        name: variation.name,
                        price: d.variationPrice(variation),
                    })),
                })),
                partners: d.activePartners
                    .filter((link: any) => link.partnerId)
                    .map((link: any) => ({
                        id: link.partnerId,
                        name: partnerName(link),
                        legalName: partnerLegalName(link),
                        total: d.partnerTotalFor(link.partnerId, d.lines),
                    })),
                partnersByType: types.map(type => {
                    const lines = d.lines
                        .map((item, line) => ({ item, line }))
                        .filter(entry => itemType(entry.item) === type)
                    const groupItems = lines.map(entry => entry.item)
                    return {
                        type,
                        lines: lines.map(entry => entry.line),
                        partners: intersectPartners(groupItems, d.effectivePartnersFor).map(link => ({
                            id: link.partnerId,
                            name: partnerName(link),
                            legalName: partnerLegalName(link),
                            total: d.partnerTotalFor(link.partnerId, groupItems),
                        })),
                    }
                }),
                partner: d.partnerId
                    ? { id: d.partnerId, name: partner ? partnerName(partner) : `Parceiro ${d.partnerId}` }
                    : null,
                total: d.chargedTotal,
                coupon: d.appliedCoupon ? { code: d.appliedCoupon.code, amount: d.appliedCoupon.amount } : null,
                grouping: d.grouping,
                addressConfirmed: d.addressConfirmed,
                address:
                    d.serviceAddress && d.serviceAddress.addressSource !== 'UNCONFIRMED'
                        ? formatAddressLine(d.serviceAddress)
                        : null,
                addressOptions:
                    addressesRef.current?.key === executantKey()
                        ? addressesRef.current.options.map(option => ({
                              id: option.id,
                              owner: option.owner,
                              label: option.address.label || null,
                              line: formatAddressLine(option.address),
                          }))
                        : [],
                payment: d.deferBilling
                    ? { later: true }
                    : d.paymentMethod
                      ? { method: d.paymentMethod, installments: d.installmentCount }
                      : payOnBillingScreenRef.current
                        ? { onBillingScreen: true }
                        : null,
                missingPrices: d.missingPrices,
                unmatchedImports: d.unmatchedImports,
                ready: d.pricingReady && !d.importingExamDoc && !d.applyingCoupon,
            }
        }

        // A tela re-renderiza depois de cada `set`: espera ela ficar pronta de novo.
        const settle = async () => {
            const started = Date.now()
            await new Promise(resolve => setTimeout(resolve, SETTLE_MIN_MS))
            while (Date.now() - started < SETTLE_MAX_MS && !snapshot().ready) {
                await new Promise(resolve => setTimeout(resolve, SETTLE_TICK_MS))
            }
        }

        /** A ação devolve um texto quando recusa antes de mexer na tela. */
        const run = async (
            action: () => Promise<string | void> | string | void,
        ): Promise<SchedulingActionResult> => {
            const { result: refusal, toasts } = await collectToasts(async () => {
                const refused = await action()
                if (!refused) await settle()
                return refused
            })
            const messages = refusal ? [`Erro: ${refusal}`, ...toasts] : toasts
            return { ok: !refusal && !toasts.some(text => text.startsWith('Erro:')), messages, state: snapshot() }
        }

        return registerScreenController('scheduling', {
            getState: snapshot,
            setClient: clientId =>
                run(() => {
                    if (!Number.isInteger(clientId) || clientId <= 0) return 'Cliente inválido.'
                    depsRef.current.setClientId(String(clientId))
                }),
            setServiceType: type =>
                run(() => {
                    if (!AI_SCHEDULING_SERVICE_TYPES.includes(type)) return `Tipo de atendimento inválido: ${type}.`
                    if (depsRef.current.serviceType === type) return
                    depsRef.current.setServiceType(type)
                }),
            searchCatalog: async query => {
                // O termo como veio e, junto, a raiz das palavras longas (ver `searchStem`):
                // os resultados do termo exato vêm primeiro.
                const { result, toasts } = await collectToasts(async () => {
                    const exact = await depsRef.current.searchAllCatalogs(query)
                    const stem = searchStem(query)
                    if (!stem) return exact
                    const byStem = await depsRef.current.searchAllCatalogs(stem)
                    const seen = new Set(exact.map(catalog => catalog.id))
                    return [...exact, ...byStem.filter(catalog => !seen.has(catalog.id))]
                })
                // Cirúrgico e pacote nunca chegam ao modelo: não há o que ele fazer com eles.
                const usable = result.flatMap(catalog => {
                    const serviceType = aiServiceTypeOf(catalog)
                    return serviceType && catalog.isSurgical !== true ? [{ catalog, serviceType }] : []
                })
                return {
                    ok: true,
                    messages: toasts,
                    results: usable.map(({ catalog, serviceType }) => ({
                        catalogId: catalog.id,
                        name: catalog.name,
                        serviceType,
                        type: catalog.catalogType?.name || '',
                        partnerLinks: (catalog.partners ?? []).length,
                        variations: ((catalog as any).variationChildren ?? [])
                            .filter((variation: any) => variation?.active !== false)
                            .map((variation: any) => ({
                                name: String(variation.name),
                                partnerLinks: (variation.partners ?? []).length,
                            })),
                    })),
                }
            },
            addItem: catalogId =>
                run(async () => {
                    const d = depsRef.current
                    const catalog = d.catalogsById[catalogId]
                    if (!catalog) return 'Item não encontrado. Busque o item antes de adicionar.'
                    if (catalog.isSurgical === true) {
                        return 'Procedimento cirúrgico não é orçado por aqui. Use ask_supervisor.'
                    }
                    const serviceType = aiServiceTypeOf(catalog)
                    if (!serviceType) return 'Este item não é exame, consulta nem procedimento.'
                    if (serviceType !== d.serviceType) {
                        return `Este item é ${serviceType} e a tela está em ${d.serviceType}. Abra a tela no tipo do item (scheduling_open com service_type ${serviceType}).`
                    }
                    if (d.lines.some(item => item.catalogId === catalogId)) return 'Este item já está no atendimento.'
                    if (d.serviceType === 'CONSULTATION' && d.lines.length > 0) {
                        return 'A consulta é um item só. Remova a consulta da linha 0 antes de pôr outra.'
                    }
                    await d.selectCatalog(catalog)
                }),
            removeItem: line =>
                run(() => {
                    const d = depsRef.current
                    if (!d.lines[line]) return `Não existe item na linha ${line}.`
                    d.removeLine(line)
                }),
            chooseVariation: (line, variationId) =>
                run(() => {
                    const d = depsRef.current
                    const item = d.lines[line]
                    if (!item) return `Não existe item na linha ${line}.`
                    const variation = (d.variationsByCatalog[item.catalogId] || []).find(entry => entry.id === variationId)
                    if (!variation) return `A variação ${variationId} não é deste item.`
                    d.chooseLineVariation(line, variation)
                }),
            importOrderFromImage: url =>
                run(async () => {
                    if (depsRef.current.serviceType !== 'EXAM') {
                        return 'A leitura do pedido médico é do agendamento de EXAME. Abra a tela com service_type EXAM.'
                    }
                    const response = await fetch(url).catch(() => null)
                    if (!response?.ok) return 'Não consegui abrir a imagem do pedido.'
                    const blob = await response.blob()
                    const name = url.split('/').pop()?.split('?')[0] || 'pedido'
                    await depsRef.current.importExamFile(new File([blob], name, { type: blob.type || 'image/jpeg' }))
                }),
            // Escolhido o executante, o retrato já traz os endereços dele (`addressOptions`).
            choosePartner: partnerId =>
                run(async () => {
                    const d = depsRef.current
                    if (!d.activePartners.some((link: any) => link.partnerId === partnerId)) {
                        return (
                            'Este parceiro não faz todos os itens que estão na tela. Se ele faz os de um tipo (partnersByType), ' +
                            'é um grupo: deixe na tela só os itens dele (scheduling_remove_item), marque-o e envie com ' +
                            'add_another=true; depois ponha os itens do próximo parceiro.'
                        )
                    }
                    d.setPartnerId(partnerId)
                    await settle()
                    await loadAddresses()
                }),
            chooseProfessional: professionalId =>
                run(async () => {
                    const d = depsRef.current
                    if (!d.professionals.some((link: any) => link.professionalId === professionalId)) {
                        return 'Este profissional não atende os itens do atendimento.'
                    }
                    d.setProfessionalId(professionalId)
                    await settle()
                    await loadAddresses()
                }),
            applyCoupon: code =>
                run(async () => {
                    const trimmed = code.trim()
                    if (!trimmed) return 'Cupom vazio.'
                    await depsRef.current.applyCoupon(trimmed)
                }),
            chargeSeparately: () =>
                run(() => {
                    depsRef.current.chargeSeparately()
                }),
            chooseAddress: addressId =>
                run(async () => {
                    const d = depsRef.current
                    if (!d.partnerId && !d.professionalId) return 'Escolha o parceiro antes do endereço.'
                    const option = (await loadAddresses()).find(entry => entry.id === addressId)
                    if (!option) return `O endereço ${addressId} não é do parceiro escolhido (veja addressOptions).`
                    d.setServiceAddress(
                        toServiceAddress(option.address, option.owner === 'partner' ? 'PARTNER' : 'PROFESSIONAL'),
                    )
                }),
            choosePayment: (method, installments) =>
                run(async () => {
                    const d = depsRef.current
                    const normalized = String(method || '').toUpperCase()
                    if (!(AI_PAYMENT_METHODS as readonly string[]).includes(normalized)) {
                        return `Forma ${method} não é lançada por você (só PIX, CREDIT_CARD e DEBIT_CARD). Parte no cartão e parte no Pix: scheduling_pay_on_billing_screen. Dinheiro ou maquininha: ask_supervisor.`
                    }
                    const count = normalized === 'CREDIT_CARD' ? Math.max(1, Math.round(Number(installments) || 1)) : 1
                    if (d.chargedTotal <= 0) return 'O atendimento ainda não tem valor. Monte os itens e escolha o parceiro antes.'
                    // A mesma cotação do bloco de pagamento: só vale o que o checkout oferece.
                    const quote = await getPaymentOptions(d.chargedTotal, [normalized as any])
                    const offered = quote.success ? (quote.result?.options ?? []) : []
                    if (!offered.some(option => option.installments === count)) {
                        return normalized === 'CREDIT_CARD'
                            ? `Cartão em ${count}x não é oferecido para este valor.`
                            : `A forma ${normalized} não está disponível para esta empresa.`
                    }
                    payOnBillingScreenRef.current = false
                    d.choosePayment(normalized, count)
                }),
            payLater: () =>
                run(() => {
                    payOnBillingScreenRef.current = false
                    depsRef.current.payLater()
                }),
            payOnBillingScreen: () =>
                run(() => {
                    payOnBillingScreenRef.current = true
                    depsRef.current.payOnBillingScreen()
                }),
            submit: async (pipelineId, addAnother) => {
                const { result, toasts } = await collectToasts(async (): Promise<SchedulingSubmitResult> => {
                    const d = depsRef.current
                    const refuse = (message: string): SchedulingSubmitResult => ({ ok: false, messages: [`Erro: ${message}`] })
                    if (d.submitting) return refuse('O envio anterior ainda está em andamento.')
                    if (!d.clientId) return refuse('Sem cliente na tela.')
                    if (d.lines.length === 0) return refuse('Sem itens na tela.')
                    if (!d.partnerId && !d.professionalId) return refuse('Escolha o parceiro (ou o profissional) antes.')
                    if (d.missingPrices.length > 0) return refuse(`Falta preço no cadastro: ${d.missingPrices.join('; ')}. Use ask_supervisor.`)
                    if (!d.serviceAddress || d.serviceAddress.addressSource === 'UNCONFIRMED') {
                        return refuse('Escolha o local de atendimento (scheduling_choose_address).')
                    }
                    // Agrupando com a cobrança que ESTA venda abriu é o grupo; com outra, é a
                    // cobrança de uma venda anterior do dia, e a nova vai à parte.
                    const inOwnGroup = !!d.grouping && d.grouping.billingId === ownGroupBillingRef.current
                    // Item que já está numa venda aberta do contato não se agenda de novo: é a
                    // venda em dobro que acontecia quando a confirmação não chegava à conversa.
                    const lineIds = new Set(d.lines.map(line => line.catalogId))
                    const duplicate = (await findOpenSales(Number(d.clientId)).catch(() => []))
                        .filter(sale => !inOwnGroup || sale.billingId !== ownGroupBillingRef.current)
                        .flatMap(sale => sale.appointments.map(entry => ({ sale, entry })))
                        .find(({ entry }) => entry.catalogIds.some(id => lineIds.has(id)))
                    if (duplicate) {
                        return refuse(
                            `Já existe venda aberta com este item: atendimento #${duplicate.entry.id}` +
                                `${duplicate.sale.billingId ? `, cobrança #${duplicate.sale.billingId}` : ''}. Não agende de novo: ` +
                                'use o link dela (VENDAS EM ABERTO). Se o contato quer mesmo outro, use ask_supervisor.',
                        )
                    }
                    if (d.grouping && !inOwnGroup) {
                        return refuse('A tela vai somar numa cobrança aberta de outra venda: use scheduling_charge_separately antes.')
                    }
                    // No grupo a forma é escolhida na tela da cobrança, depois do último.
                    const paymentDecided = d.deferBilling || !!d.paymentMethod || payOnBillingScreenRef.current
                    if (!addAnother && !inOwnGroup && !paymentDecided) {
                        return refuse('Escolha a forma de pagamento (scheduling_choose_payment), pagar depois ou dividir na tela da cobrança.')
                    }
                    if (addAnother && d.deferBilling) {
                        return refuse('"Pagar depois" não vale para um grupo: o grupo nasce numa cobrança só.')
                    }
                    const deferred = d.deferBilling
                    const toBillingScreen = !addAnother && (inOwnGroup || payOnBillingScreenRef.current)
                    if (pipelineId) {
                        // O funil entra antes do envio: o `submit` lido depois do `settle` já é o
                        // da tela re-renderizada, com o funil novo.
                        d.setPipelineId(pipelineId)
                        await settle()
                    }
                    const created = await depsRef.current.submit(addAnother)
                    if (!created) return { ok: false, messages: [] }
                    const output: SchedulingSubmitResult = { ok: true, messages: [], created: createdFromPath(created.detailPath) }
                    if (addAnother) {
                        ownGroupBillingRef.current = created.billingId
                        output.addedToGroup = true
                        // A tela limpa itens, executante e local e procura de novo a cobrança.
                        await settle()
                        return output
                    }
                    ownGroupBillingRef.current = null
                    payOnBillingScreenRef.current = false
                    if (!created.billingId) {
                        output.billingDeferred = deferred
                        return output
                    }
                    if (toBillingScreen) {
                        output.nextScreen = 'billing'
                        return output
                    }
                    const billingResponse = await getBilling(created.billingId)
                    const billing: any = billingResponse.success ? billingResponse.result : null
                    if (!billing) {
                        output.messages.push('Agendado, mas não consegui ler a cobrança. Use ask_supervisor para o link.')
                        return output
                    }
                    const method: string | null = billing.paymentMethod ?? null
                    output.billing = {
                        id: billing.id,
                        amount: billing.amount,
                        paymentMethod: method,
                        installments: billing.installmentCount ?? null,
                        checkoutUrl:
                            billing.publicToken && (!method || CHECKOUT_LINK_METHODS.includes(method))
                                ? `${window.location.origin}/pay/${billing.publicToken}`
                                : null,
                        pixCopyPaste: billing.pixCopyPaste ?? null,
                    }
                    return output
                })
                return { ...result, messages: [...result.messages, ...toasts], ok: result.ok && !toasts.some(text => text.startsWith('Erro:')) }
            },
        })
    }, [deps.enabled])
}
